import { useMemo } from "react";

import { TasksDashboardGroupRow } from "../../../../../widget";
import { TasksDashboardGroup, TasksDashboardQueryFilters } from "../models";
import { getGroupTasksKey } from "../utils";
import { TASKS_DASHBOARD_GANTT_PAGE_SIZE } from "./constants";
import { useWorkflowTaskGroups } from "./useWorkflowTaskGroups";

export interface UseTasksDashboardGanttArgs {
  /** The one shared task-level filter object (BR-02828). Must be referentially stable. */
  filters: TasksDashboardQueryFilters;
  /** False unless the Gantt is the view on screen, so no group query is issued for other views. */
  enabled: boolean;
  /** Rows per group page. Defaults to the chart's reveal step, so one click means one page of rows. */
  pageSize?: number;
  refreshKey?: number;
}

export interface UseTasksDashboardGanttReturnData {
  /** Group rows in the shape the dashboard widget passes to the Gantt. */
  ganttGroups: TasksDashboardGroupRow[];
  /** The raw group headers, for anything that needs the per-status counts. */
  groups: TasksDashboardGroup[];
  isLoadingGroups: boolean;
  groupsHaveNextPage: boolean;
  fetchMoreGroups: () => void;
  /** Loads a group's rows — called when the Gantt expands it. */
  onGroupExpand: (objectUuid: string) => void;
  /** Loads a group's next page of rows. */
  onGroupShowMore: (objectUuid: string) => void;
}

/**
 * The Gantt view's data: server group headers plus each group's rows, loaded when the group is
 * expanded. A Gantt row spans a whole object, so its leaves are loaded without a status slice —
 * unlike the List view, which loads one status section at a time.
 */
export const useTasksDashboardGantt = ({
  filters,
  enabled,
  pageSize = TASKS_DASHBOARD_GANTT_PAGE_SIZE,
  refreshKey,
}: UseTasksDashboardGanttArgs): UseTasksDashboardGanttReturnData => {
  const {
    groups,
    isLoadingGroups,
    groupsHaveNextPage,
    fetchMoreGroups,
    groupTasks,
    loadGroupTasks,
    fetchMoreGroupTasks,
  } = useWorkflowTaskGroups({ filters, enabled, pageSize, refreshKey });

  const ganttGroups = useMemo<TasksDashboardGroupRow[]>(
    () =>
      groups.map((group) => {
        const leaves = groupTasks[getGroupTasksKey(group.objectUuid)];

        return {
          objectUuid: group.objectUuid,
          objectName: group.objectName ?? "",
          objectType: group.objectType,
          // Server counts, so a collapsed or part-loaded group still reports its real size
          // (BR-02826/02827) — including the overdue badge, which is a deadline comparison the
          // group query resolves over the whole filtered set rather than the loaded rows.
          taskCount: group.taskCount,
          milestoneTaskCount: group.milestoneTaskCount,
          overdueTaskCount: group.overdueTaskCount,
          // Keyed by lane, so the chart labels a count exactly as it labels the rows beneath it.
          countsByColumnId: group.countsByColumnId,
          // The row query's own total, which is what the "N remaining" on its "Show more" counts down.
          // The header's `taskCount` answers a different query and can legitimately differ.
          pagedTaskCount: leaves?.totalCount,
          tasks: leaves?.tasks ?? [],
          isLoadingTasks: leaves?.isLoading ?? false,
          hasMoreTasks: leaves?.hasNextPage ?? false,
        };
      }),
    [groups, groupTasks]
  );

  return {
    ganttGroups,
    groups,
    isLoadingGroups,
    groupsHaveNextPage,
    fetchMoreGroups,
    onGroupExpand: loadGroupTasks,
    onGroupShowMore: fetchMoreGroupTasks,
  };
};
